import React, { useState, useRef, useEffect } from 'react';
import { Send, Bot, User, Minimize2, Maximize2, Sparkles, MessageSquare, HelpCircle } from 'lucide-react';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { useAuth } from '../../hooks/useAuth';
import { aiService } from '../../lib/ai/aiService';

interface Message {
  id: string;
  type: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface QuickAction {
  id: string;
  label: string;
  query: string;
  icon: React.ComponentType<any>;
}

export const ClientAIAssistant: React.FC = () => {
  const { user } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const quickActions: QuickAction[] = [
    {
      id: 'invoices',
      label: 'Show my invoices',
      query: 'Show me my invoices and their status',
      icon: MessageSquare
    },
    {
      id: 'payments',
      label: 'Payment history',
      query: 'Show me my payment history',
      icon: MessageSquare
    },
    {
      id: 'quotes',
      label: 'Pending quotes',
      query: 'Do I have any pending quotes to review?',
      icon: MessageSquare
    },
    {
      id: 'help',
      label: 'How to pay invoices',
      query: 'How do I pay my invoices?',
      icon: HelpCircle
    }
  ];

  // Welcome message
  useEffect(() => {
    if (messages.length === 0) {
      const welcomeMessage: Message = {
        id: 'welcome',
        type: 'assistant',
        content: `Hello ${user?.full_name || 'there'}! I'm your AI assistant. I can help you with questions about your invoices, quotes, payments, and how to use the client portal. What would you like to know?`,
        timestamp: new Date(),
      };
      setMessages([welcomeMessage]);
    }
  }, [user?.full_name, messages.length]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when not minimized
  useEffect(() => {
    if (!isMinimized) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isMinimized]);

  const handleSendMessage = async (messageText?: string) => {
    const textToSend = messageText || inputValue.trim();
    if (!textToSend || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: textToSend,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      // Simulate AI response with a delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      let response = '';
      
      // Simple pattern matching for demo purposes
      const lowerText = textToSend.toLowerCase();
      
      if (lowerText.includes('invoice') && lowerText.includes('status')) {
        response = `You have 3 invoices in total:
- 1 paid invoice
- 1 pending invoice
- 1 overdue invoice

Your most recent invoice is INV-2301-001 for ₹12,500, due on February 15, 2025.`;
      } else if (lowerText.includes('payment') && lowerText.includes('history')) {
        response = `Your payment history shows:
- January 15, 2025: ₹8,500 (Card payment)
- December 10, 2024: ₹15,000 (Bank transfer)

Total payments made: ₹23,500`;
      } else if (lowerText.includes('pending') && lowerText.includes('quote')) {
        response = `Yes, you have 1 quote awaiting your review:
- QUO-2301-002 for ₹18,750, valid until March 1, 2025

You can approve or reject this quote from the Quotes section.`;
      } else if (lowerText.includes('pay') && lowerText.includes('invoice')) {
        response = `To pay an invoice:
1. Go to the Invoices section
2. Find the invoice you want to pay
3. Click the "Pay Now" button
4. Choose your preferred payment method
5. Complete the payment process

We accept credit cards, bank transfers, UPI, and other payment methods.`;
      } else {
        response = `I'm here to help with your questions about invoices, quotes, payments, and using the client portal. 

Some things you can ask me about:
- Invoice status and details
- Payment history
- Pending quotes
- How to use portal features

How else can I assist you today?`;
      }
      
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: response,
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('AI Assistant error:', error);
      
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: "I apologize, but I'm experiencing some technical difficulties. Please try again in a moment, or contact support if the issue persists.",
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickAction = (action: QuickAction) => {
    handleSendMessage(action.query);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const clearChat = () => {
    if (window.confirm('Are you sure you want to clear the chat history?')) {
      setMessages([{
        id: 'welcome-new',
        type: 'assistant',
        content: `Hello ${user?.full_name || 'there'}! I'm your AI assistant. How can I help you today?`,
        timestamp: new Date(),
      }]);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-2xl flex items-center justify-center shadow-lg">
          <Bot className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">AI Assistant</h1>
          <p className="text-gray-600 mt-1">Get instant help with your account and portal features</p>
        </div>
      </div>

      {/* Quick Actions */}
      <Card>
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Sparkles className="w-5 h-5 mr-2 text-primary-600" />
          Quick Actions
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {quickActions.map((action) => {
            const Icon = action.icon;
            return (
              <button
                key={action.id}
                onClick={() => handleQuickAction(action)}
                className="flex items-center space-x-3 p-3 text-left bg-gray-50 hover:bg-primary-50 rounded-lg transition-colors duration-200 group"
                disabled={isLoading}
              >
                <Icon className="w-5 h-5 text-gray-500 group-hover:text-primary-600" />
                <span className="text-gray-700 group-hover:text-primary-700 font-medium">
                  {action.label}
                </span>
              </button>
            );
          })}
        </div>
      </Card>

      {/* Chat Interface */}
      <Card className={`transition-all duration-300 ${isMinimized ? 'h-20' : 'h-[600px]'}`}>
        {/* Chat Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-primary-50 to-secondary-50">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
              <Bot className="w-6 h-6 text-primary-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">AI Assistant Chat</h3>
              <p className="text-xs text-gray-500">
                {isLoading ? 'Thinking...' : 'Online and ready to help'}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={clearChat}
              className="text-gray-500"
            >
              Clear Chat
            </Button>
            <button
              onClick={() => setIsMinimized(!isMinimized)}
              className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
            >
              {isMinimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {!isMinimized && (
          <>
            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 h-[440px]">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`flex items-start space-x-2 max-w-[80%] ${message.type === 'user' ? 'flex-row-reverse space-x-reverse' : ''}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      message.type === 'user' 
                        ? 'bg-primary-600' 
                        : 'bg-secondary-100'
                    }`}>
                      {message.type === 'user' ? (
                        <User className="w-4 h-4 text-white" />
                      ) : (
                        <Bot className="w-4 h-4 text-secondary-600" />
                      )}
                    </div>
                    <div className={`rounded-lg p-3 ${
                      message.type === 'user'
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 text-gray-900'
                    }`}>
                      <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                      <p className={`text-xs mt-1 ${
                        message.type === 'user' ? 'text-primary-200' : 'text-gray-500'
                      }`}>
                        {formatTime(message.timestamp)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
              
              {isLoading && (
                <div className="flex justify-start">
                  <div className="flex items-start space-x-2 max-w-[80%]">
                    <div className="w-8 h-8 bg-secondary-100 rounded-full flex items-center justify-center">
                      <Bot className="w-4 h-4 text-secondary-600" />
                    </div>
                    <div className="bg-gray-100 rounded-lg p-3">
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-4 border-t border-gray-200">
              <div className="flex space-x-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Ask me about your invoices, payments, or how to use the portal..."
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                  disabled={isLoading}
                />
                <Button
                  size="sm"
                  icon={Send}
                  onClick={() => handleSendMessage()}
                  disabled={!inputValue.trim() || isLoading}
                  className="px-3"
                >
                </Button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                I can help with questions about your account data and portal features.
              </p>
            </div>
          </>
        )}
      </Card>

      {/* Help Tips */}
      <Card className="bg-blue-50 border-blue-200">
        <h3 className="text-blue-900 font-semibold mb-2 flex items-center">
          <HelpCircle className="w-5 h-5 mr-2" />
          What can I help you with?
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-blue-800 text-sm">
          <div>
            <p>• Check invoice status and amounts</p>
            <p>• View payment history</p>
            <p>• Approve or reject quotes</p>
          </div>
          <div>
            <p>• Learn how to use portal features</p>
            <p>• Get account information</p>
            <p>• Find answers to common questions</p>
          </div>
        </div>
      </Card>
    </div>
  );
};